<template><div><dt-notice kind="info" :show-close="false" class="d-wmx100p d-my-200 dialtone-doc-notice">
Not a standalone component, please use Message Input recipe instead
</dt-notice>
<component-combinator component-name="DtRichTextEditor" /><h2 id="base-style" tabindex="-1" class="d-docsite--header-2">Base Style <a class="header-anchor d-link d-docsite--link d-link" href="#base-style" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">The editor itself is without any styling and the intention is to wrap it with another component, such as Message Input, that provides the UI.</p>
<code-example source-code='&lt;dt-rich-text-editor
  v-model=&quot;value&quot;
  :editable=&quot;true&quot;
  input-aria-label=&quot;this is a descriptive label&quot;
  :input-class=&quot;$attrs.inputClass&quot;
  :output-format=&quot;$attrs.outputFormat&quot;
  :auto-focus=&quot;false&quot;
  placeholder=&quot;Type here...&quot;
  :link=&quot;true&quot;
/&gt;'>
<example-rich-text-editor :modelValue="defaultValue" />
</code-example>
<h2 id="with-links" tabindex="-1" class="d-docsite--header-2">With Links <a class="header-anchor d-link d-docsite--link d-link" href="#with-links" aria-hidden="true">#</a></h2>
<code-example only-show="demo">
<example-rich-text-editor
  modelValue="<p>The editor can autolink URLs: <a target='_blank' rel='noopener noreferrer nofollow' class='d-link d-wb-break-all' href='http://dialpad.com'>dialpad.com</a>, <a target='_blank' rel='noopener noreferrer nofollow' class='d-link d-wb-break-all' href='https://www.dialpad.com/about-us/'>https://www.dialpad.com/about-us/</a>, email addresses: <a target='_blank' rel='noopener noreferrer nofollow' class='d-link d-wb-break-all' href='mailto:noreply@dialpad.com'>noreply@dialpad.com</a></p>"
/>
</code-example>
<h2 id="output-format" tabindex="-1" class="d-docsite--header-2">Output Format <a class="header-anchor d-link d-docsite--link d-link" href="#output-format" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">There are 3 defined output formats currently for the input text.</p>
<h3 id="text" tabindex="-1" class="d-docsite--header-3">Text <a class="header-anchor d-link d-docsite--link d-link" href="#text" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Provide prop <code v-pre>outputFormat: 'text'</code></p>
<p class="d-docsite--paragraph">Plain text format as output without taking into consideration any marks (italics, bold) / html tags/ codeblocks
that may be included in the input text user provides.</p>
<h3 id="json" tabindex="-1" class="d-docsite--header-3">Json <a class="header-anchor d-link d-docsite--link d-link" href="#json" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Provide prop <code v-pre>outputFormat: 'json'</code></p>
<p class="d-docsite--paragraph">the input will be formatted as a json document that can then be consumed by product.
The type of the each node in the input will be present for example: <code v-pre>codeblock</code>, <code v-pre>image</code>
along with other meta data pertaining to the text node.</p>
<h3 id="html" tabindex="-1" class="d-docsite--header-3">HTML <a class="header-anchor d-link d-docsite--link d-link" href="#html" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Provide prop <code v-pre>outputFormat: 'html'</code></p>
<p class="d-docsite--paragraph">the input will be formatted as native html that can then be consumed by product.</p>
<h2 id="tiptap-plugins-used" tabindex="-1" class="d-docsite--header-2">Tiptap Plugins Used <a class="header-anchor d-link d-docsite--link d-link" href="#tiptap-plugins-used" aria-hidden="true">#</a></h2>
<h3 id="hardbreak" tabindex="-1" class="d-docsite--header-3"><a href="https://tiptap.dev/api/nodes/hard-break" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">HardBreak</a> <a class="header-anchor d-link d-docsite--link d-link" href="#hardbreak" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">HardBreak plugin is used to add support for <code v-pre>&lt;br&gt;</code> tags in the rich text editor.</p>
<h3 id="codeblocks" tabindex="-1" class="d-docsite--header-3"><a href="https://tiptap.dev/api/nodes/code-block" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">CodeBlocks</a> <a class="header-anchor d-link d-docsite--link d-link" href="#codeblocks" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Codeblock plugin is used to enable the support for code and also specify the language. Type ``` followed by space
or ~~~ with space to enable a codeblock in the rich text editor.</p>
<h3 id="placeholder" tabindex="-1" class="d-docsite--header-3"><a href="https://tiptap.dev/api/extensions/placeholder" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">Placeholder</a> <a class="header-anchor d-link d-docsite--link d-link" href="#placeholder" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Provides support to enable placeholder support</p>
<h3 id="mention" tabindex="-1" class="d-docsite--header-3"><a href="https://tiptap.dev/api/nodes/mention" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">Mention</a> <a class="header-anchor d-link d-docsite--link d-link" href="#mention" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">The tiptap Mention plugin is apt for our use case of mentioning users or channels within the editor.
It also creates the respective nodes with ability for user to customize the trigger character.</p>
<h4 class="d-docsite--header-4">How to Use the Mention Plugin:</h4>
<p class="d-docsite--paragraph">When using the Rich Text editor, the developer would have to pass in the suggestion object prop
which MUST contain an items function that takes in a query and returns a list of object with the properties:</p>
<div class="language-json" data-highlighter="prismjs" data-ext="json"><pre v-pre><code class="language-json"><span class="line"><span class="token punctuation">{</span></span>
<span class="line">  id<span class="token operator">:</span> String<span class="token punctuation">,</span></span>
<span class="line">  name<span class="token operator">:</span> String<span class="token punctuation">,</span></span>
<span class="line">  avatarSrc<span class="token operator">:</span> String</span>
<span class="line"><span class="token punctuation">}</span></span>
<span class="line"></span></code></pre>
</div><p class="d-docsite--paragraph">There can be additional properties that override the mention plugin and
more details can be seen here: <a href="https://tiptap.dev/api/utilities/suggestion" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">Suggestion Tiptap utility</a>.
If this mentionSuggestion Object prop is not supplied, the mention plugin is disabled by default.</p>
<p class="d-docsite--paragraph">To see it in action type char '@' into rich editor With channel mentions.</p>
<code-example source-code='&lt;dt-rich-text-editor
  v-model=&quot;value&quot;
  :editable=&quot;true&quot;
  input-aria-label=&quot;this is a descriptive label&quot;
  :input-class=&quot;$attrs.inputClass&quot;
  :output-format=&quot;$attrs.outputFormat&quot;
  :auto-focus=&quot;false&quot;
  placeholder=&quot;Type here...&quot;
  :link=&quot;true&quot;
  :mentionSuggestion=&quot;{ items }&quot;
/&gt;'>
<example-rich-text-editor
  modelValue="<p>The editor can also suggest mentions: <mention-component name='Test Person' avatarsrc='' id='test.person'></mention-component>, <mention-component name='Test Person 2' avatarsrc='' id='test.person2'></mention-component>! The suggestions dropdown will wait 1000ms to simulate an API call.</p>"
  :mentionSuggestion="{ items }"
/>
</code-example>
<h2 id="vue-api" tabindex="-1" class="d-docsite--header-2">Vue API <a class="header-anchor d-link d-docsite--link d-link" href="#vue-api" aria-hidden="true">#</a></h2>
<component-vue-api component-name="richtexteditor" /></div></template>


<script setup>
import ExampleRichTextEditor from '@exampleComponents/ExampleRichTextEditor.vue';

const defaultValue = `<p>I am not a standalone component, please use Message Input instead <emoji-component code=":v_tone3:">
      </emoji-component><emoji-component code=":robot:"></emoji-component>!</p>`;

const CONTACT_LIST = [
  {
    id: 'test.person',
    name: 'Test Person',
    avatarSrc: 'https://avatars.githubusercontent.com/u/13851061?s=460&u=1f1b5b0b5b2b2b2b2b2b2b2b2b2b2b2b2b2b2b&v=4',
  },
  {
    id: 'test.person2',
    name: 'Test Person 2',
    avatarSrc: 'https://avatars.githubusercontent.com/u/13851061?s=460&u=1f1b5b0b5b2b2b2b2b2b2b2b2b2b2b2b2b2b2b&v=4',
  },
  {
    id: 'test.person3',
    name: 'Test Person 3',
    avatarSrc: 'https://avatars.githubusercontent.com/u/13851061?s=460&u=1f1b5b0b5b2b2b2b2b2b2b2b2b2b2b2b2b2b2b&v=4',
  },
  {
    id: 'brad.paugh',
    name: 'Brad Paugh',
    avatarSrc: 'https://avatars.githubusercontent.com/u/13851061?s=460&u=1f1b5b0b5b2b2b2b2b2b2b2b2b2b2b2b2b2b2b&v=4',
  },
  {
    id: 'bradley.hawkins',
    name: 'Bradley Hawkins',
    avatarSrc: 'https://avatars.githubusercontent.com/u/13851061?s=460&u=1f1b5b0b5b2b2b2b2b2b2b2b2b2b2b2b2b2b2b&v=4',
  },
  {
    id: 'julio.ortega',
    name: 'Tico Ortega',
    avatarSrc: 'https://avatars.githubusercontent.com/u/13851061?s=460&u=1f1b5b0b5b2b2b2b2b2b2b2b2b2b2b2b2b2b2b&v=4',
  },
  {
    id: 'ignacio.Ropolo',
    name: 'Ignacio Ropolo',
    avatarSrc: 'https://avatars.githubusercontent.com/u/13851061?s=460&u=1f1b5b0b5b2b2b2b2b2b2b2b2b2b2b2b2b2b2b&v=4',
  },
  {
    id: 'nina.repetto',
    name: 'Nina Repetto',
    avatarSrc: 'https://avatars.githubusercontent.com/u/13851061?s=460&u=1f1b5b0b5b2b2b2b2b2b2b2b2b2b2b2b2b2b2b&v=4',
  },
  {
    id: 'long.name',
    name: 'LongnameLongnameLongnameLongnameLongnameLongnameLongnameLongnameLongnameLongnameLongnameLongnameLongnameLongnameLongname',
    avatarSrc: 'https://avatars.githubusercontent.com/u/13851061?s=460&u=1f1b5b0b5b2b2b2b2b2b2b2b2b2b2b2b2b2b2b&v=4',
  },
  {
    id: 'long.name.with.spaces',
    name: 'Long Name Long Name Long Name Long NameLong Name Long Name Long Name Long NameLong Name Long Name Long Name Long NameLong Name Long Name Long Name Long NameLong Name Long Name Long Name Long Name ',
    avatarSrc: 'https://avatars.githubusercontent.com/u/13851061?s=460&u=1f1b5b0b5b2b2b2b2b2b2b2b2b2b2b2b2b2b2b&v=4',
  },
];

async function items ({ query }) {
  // simulate an API call by waiting 1000 seconds.
  await new Promise(resolve => setTimeout(resolve, 1000));

  if (query.length === 0) return CONTACT_LIST;
  return CONTACT_LIST.filter((contact) => { return contact.name.toLowerCase().startsWith(query.toLowerCase()); });
};

</script>